import {LatestUpdateType} from './latestupdate';

export enum UserRole {
    CAN_ADD_MULTIPLE_MEDIA_SECTIONS = 'CAN_ADD_MULTIPLE_MEDIA_SECTIONS',
    CAN_ADD_CATALOG = 'CAN_ADD_CATALOG',
}

export enum PageType {
    LANDING_PAGE = 'LANDING_PAGE',
    DASHBOARD_SCREEN = 'DASHBOARD_SCREEN',
}

export type WebsiteSource = 'WEBSITE' | 'DASHBOARD';

export enum NodeType {
    ComponentNode = 'ComponentNode',
    TextNode = 'TextNode',
    MediaNode = 'MediaNode',
    CatalogNode = 'CatalogNode',
}

export enum SectionType {
    HERO = 'HERO',
    ABOUT = 'ABOUT',
    CONTACT = 'CONTACT',
    MEDIA = 'MEDIA',
    TESTIMONIALS = 'TESTIMONIALS',
    LATEST_UPDATE = 'LATEST_UPDATE',
    CATALOG = 'CATALOG',
}

export enum TextSubType {
    TITLE,
    CONTENT,
}

export enum Status {
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
}

export enum MediaType {
    IMAGE = 'IMAGE',
    VIDEO = 'VIDEO',
}

export interface ContactInfo {
    addressString: TextComponent;
    contactNumber: TextComponent;
    googleMapsLink: TextComponent;
    whatsappNumber: TextComponent;
    shopName: TextComponent;
    facebookLink: TextComponent;
    instagramLink: TextComponent;
}

interface INode {
    id: string;
    type: NodeType;
}

interface TextComponent extends INode {
    text: string;
}

export interface WebsiteStructure extends INode {
    sections: Section[];
    status: Status;
    subDomain: string;
}

export type BackgroundType = 'BackgroundImage' | 'BackgroundColor';

export interface IBackground {
    type: BackgroundType;
}

export interface BackgroundImageInfo extends IBackground {
    backgroundImageUrl: string;
}

export interface BackgroundColorInfo extends IBackground {
    backgroundColor: string;
}

export interface Section extends INode {
    sectionType: SectionType;
    title: TextComponent;
    content?: TextComponent;
    backgroundInfo?: IBackground;
}

export interface HeroSection extends Section {
    contactInfo?: ContactInfo;
}

export interface AboutSection extends Section {}

export interface MediaComponent extends INode {
    mediaType: MediaType;
    url: string;
    status: Status;
    text?: string;
}

export interface MediaSection extends Section {
    mediaContent: MediaComponent[];
}

export interface ContactSection extends Section {
    contactInfo?: ContactInfo;
}

export interface LatestUpdateItemDTO {
    id: string;
    createdAt: number;
    status: Status;
    updateType: LatestUpdateType;
}

export interface ItemTextLatestUpdateDTO extends LatestUpdateItemDTO {
    text: TextComponent;
}

export interface ItemImageLatestUpdateDTO extends LatestUpdateItemDTO {
    url: string;
}

export interface ItemVideoLatestUpdateDTO extends LatestUpdateItemDTO {
    url: string;
}

export interface LatestUpdateSection extends Section {
    latestUpdate: LatestUpdateItemDTO[];
}

export interface TestimonialComponentDTO {
    id: string;
    displayName: TextComponent;
    feedbackContent: TextComponent;
    displayImage: MediaComponent;
}

export interface TestimonialsSection extends Section {
    testimonials: TestimonialComponentDTO[];
}

export interface CatalogComponent extends INode {
    image: MediaComponent;
    title: TextComponent;
    subtitle: TextComponent;
    status: Status;
    price: TextComponent;
}

export interface CatalogSection extends Section {
    catalogComponents: CatalogComponent[];
}

// request models

export interface EditTextContentDTO {
    id: string;
    text: string;
    sectionId?: string;
}

export interface EditRequestDTO {
    updates: EditTextContentDTO[];
}

export interface OnboardingRequestDTO {
    shopName: string;
    websiteTagline: string;
    aboutTheShop: string;
    contactNumber: string;
    whatsappNumber: string;
    googleMapsLink: string;
    addressString: string;
    subDomain: string;
}

export interface HttpResponse {
    message: string;
    statusCode: number;
}

export enum APIResponseType {
    ErrorString = 'ErrorString',
    SuccessfulString = 'SuccessfulString',
    WebsiteStructure = 'WebsiteStructure',
    RedirectParams = 'RedirectParams',
    SuccessStateWithAction = 'SuccessStateWithAction',
    OrderDetails = 'OrderDetails',
    OrderInfoByStatus = 'OrderInfoByStatus',
}

export interface APIResponse {
    data: any;
    type: APIResponseType;
}

export interface UserDTO {
    name: string;
    mobileNumber: string;
    id: string;
    roles: UserRole[];
}

export interface UserResponse {
    data: UserDTO;
    type: APIResponseType;
}

export interface RedirectParams {
    screenName: string;
    path: string;
    queryParams: Map<String, String>;
}

// update remove testimonial request dto
export interface UpdateTestimonialDTO {
    action: 'ADD' | 'REMOVE';
}

export interface AddTestimonialDTO extends UpdateTestimonialDTO {
    displayName: string;
    feedbackText: string;
}

export interface RemoveTestimonialDTO extends UpdateTestimonialDTO {
    id: string;
}

// Update Website Structure

export interface WebsiteSectionUpdateDTO {
    sectionType: SectionType;
    status: Status;
}

export interface EditWebsiteRequestDTO {
    updates: WebsiteSectionUpdateDTO[];
}

export interface UserSession {
    id: string;
    name: string;
    roles: UserRole[];
    doesUserHaveWebsite: boolean;
}

export interface CartItemPayload {
    id: string;
    title: string;
    price: number;
    quantity: number;
}

export interface LocalCart {
    id: string;
    cartItems: Map<string, CartItemPayload>;
}

export enum WebsiteAction {
    SnackbarWithRedirect = 'SnackbarWithRedirect',
    SnackbarWithNewPage = 'SnackbarWithNewPage',
    PageRefresh = 'PageRefresh',
}

export interface SuccessStateWithAction {
    message: string;
    action: WebsiteAction;
    actionDetails: RedirectParams;
}

export type SupportedDashboardActionTypes = 'share' | 'delete' | 'orders-dashboard' | 'payments-dashboard';

// cart models

export interface UpdateCartItem {
    catalogId: string;
    quantity: number;
}

export interface BuyerDTO {
    name: string;
    email: string;
    mobileNumber: string;
    address: string;
}

export interface FetchCartDTO {
    items: UpdateCartItem[];
    websiteId: string;
    buyerDTO: BuyerDTO;
    cartId?: string | undefined;
}

export enum CartViolationType {
    MISSING_ITEM_IN_CATALOG = 'MISSING_ITEM_IN_CATALOG',
    CATALOG_ITEM_INACTIVE = 'CATALOG_ITEM_INACTIVE',
    WEBSITE_INACTIVE = 'WEBSITE_INACTIVE',
    WEBSITE_USER_ROLE_INVALID = 'WEBSITE_USER_ROLE_INVALID',
    MISSING_PAYMENT_INFORMATION = 'MISSING_PAYMENT_INFORMATION',
}

export interface CartViolation {
    type: CartViolationType;
}

export interface InactiveCatalogItemViolation extends CartViolation {
    catalogId: string;
    websiteId: string;
}

export interface MissingItemInCatalogViolation extends CartViolation {
    catalogId: string;
    websiteId: string;
}

export interface InactiveWebsiteViolation extends CartViolation {
    id: string;
    userId: string;
}

export interface WebsiteUserRoleInvalidViolation extends CartViolation {
    userId: string;
}

enum MissingPaymentViolationState {
    PAYMENT_INFO_NOT_ADDED = 'PAYMENT_INFO_NOT_ADDED',
    UPI_ID_IS_BLANK = 'UPI_ID_IS_BLANK',
}

export interface MissingPaymentInformationViolation extends CartViolation {
    userId: string;
    state: MissingPaymentViolationState;
}

export interface CartItem {
    id: string;
    title: string;
    quantity: number;
    priceInPaise: number;
}

export interface CartPaymentDTO {
    cartAmount: number;
    gstAmount: number;
    finalAmount: number;
    receiverVPA: string;
    displayName: string;
}

export interface CartDTO {
    cartId: string;
    websiteId: string;
    violations: CartViolation[];
    items: CartItem[];
    paymentDetails: CartPaymentDTO;
    buyerDTO: BuyerDTO;
}

export interface CheckoutCartDTO {
    items: UpdateCartItem[];
    websiteId: string;
    transactionID: string;
    customerVPA: string;
    paymentDetails: CartPaymentDTO;
    cartId: string;
    buyerDTO: BuyerDTO;
    createdOn: number;
}

export enum OrderStatus {
    CREATED = 'CREATED',
    PAYMENT_CONFIRMED = 'PAYMENT_CONFIRMED',
    ORDER_CONFIRMED = 'ORDER_CONFIRMED',
    ORDER_DECLINED = 'ORDER_DECLINED',
    PAYMENT_REFUNDED = 'PAYMENT_REFUNDED',
    ORDER_COMPLETED = 'ORDER_COMPLETED',
    ORDER_CANCELLED = 'ORDER_CANCELLED',
}

export interface OrderInfo {
    websiteId: string;
    id: string;
    cartId: string;
    items: CartItem[];
    transactionID: string;
    ownerVPA: string;
    customerVPA: string;
    paymentDetails: CartPaymentDTO;
    buyerDTO: BuyerDTO;
    orderStatus: OrderStatus;
    createdAt: number;
    ownerName: string;
}

interface ShopDetails {
    name: string;
    websiteUrl: string;
}

export interface OrderDetail {
    orderId: string;
    shopDetails: ShopDetails;
    buyerDTO: BuyerDTO;
    items: CartItem[];
    paymentDetails: CartPaymentDTO;
    customerVPA: string;
    statusMessage: string;
    transactionID: string;
    createdAt: number;
}

export interface OrderInfoByStatus {
    orders: Record<OrderStatus, OrderInfo[]>;
}

export interface OrderStatusUpdateRequestDTO {
    orderId: string;
    status: OrderStatus;
}

export enum MetaDataKeys {
    paymentVpa = 'PAYMENT_VPA',
}

export interface MetaPaymentInfo {
    type: MetaDataKeys;
}

export interface UPIDataInfo extends MetaPaymentInfo {
    type: MetaDataKeys.paymentVpa;
    upiId: string;
}

export type MetaInfoType = UPIDataInfo;

export interface CampaignInfo {
    campaignSource: string | null;
    campaignId: string | null;
}

export interface UserCreationRequestDTO {
    mobileNumber: string;
    name: string;
    ipAddress?: string;
    referralCode?: string;
    emailIdPrimary?: string;
    campaignInfo?: CampaignInfo;
}
